import React from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./lib/auth";
import RequireAuth from "./components/RequireAuth";
import ErrorBoundary from "./components/ErrorBoundary";
import Header from "./components/Header";
import Footer from "./components/Footer";
// Phase 17/18 redesign — global toast + theme toggle + skeleton styles
import { Toaster } from "./components/Toaster";
import { ThemeProvider } from "./components/ThemeProvider";
import { SkeletonStyles } from "./components/Skeleton";

// Phase 20 — code-splitting: keep Home eager (it's the landing page —
// users hit it first, lazy-loading would add a spinner flash), every
// other route lazy-loaded so the initial bundle stays small.
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";

const About          = React.lazy(() => import("./pages/About"));
const Download       = React.lazy(() => import("./pages/Download"));
const Browse         = React.lazy(() => import("./pages/Browse"));
const PluginDetail   = React.lazy(() => import("./pages/PluginDetail"));
const CreatorProfile = React.lazy(() => import("./pages/CreatorProfile"));
const Creators       = React.lazy(() => import("./pages/Creators"));
const Publish        = React.lazy(() => import("./pages/Publish"));
const Dashboard      = React.lazy(() => import("./pages/Dashboard"));
const Moderation     = React.lazy(() => import("./pages/Moderation"));
const Auth           = React.lazy(() => import("./pages/Auth"));
const VerifyEmail    = React.lazy(() => import("./pages/VerifyEmail"));
const OAuthCallback  = React.lazy(() => import("./pages/OAuthCallback"));
const Pricing        = React.lazy(() => import("./pages/Pricing"));
const PaymentResult  = React.lazy(() => import("./pages/PaymentResult"));
const Docs           = React.lazy(() => import("./pages/Docs"));
const Settings       = React.lazy(() => import("./pages/Settings"));
const Workflows      = React.lazy(() => import("./pages/Workflows"));
const WorkflowEditor = React.lazy(() => import("./pages/WorkflowEditor"));
const Privacy        = React.lazy(() => import("./pages/Privacy"));
const Terms          = React.lazy(() => import("./pages/Terms"));
const Contact        = React.lazy(() => import("./pages/Contact"));

// Shared Suspense fallback — small spinner so route transitions don't
// flash a blank page. Stays mounted only until the chunk arrives.
function RouteFallback() {
  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-20 flex items-center justify-center">
      <div className="flex items-center gap-3 text-text2 font-mono text-sm">
        <div className="w-4 h-4 border-2 border-accent border-r-transparent rounded-full animate-spin" />
        loading…
      </div>
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <SkeletonStyles />
            <Toaster />
            <Header />
          <main className="min-h-[calc(100vh-4rem)]">
            <ErrorBoundary>
              <React.Suspense fallback={<RouteFallback />}>
              <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/download" element={<Download />} />
              <Route path="/browse" element={<Browse />} />
              <Route path="/plugin/:slugOrId" element={<PluginDetail />} />
              <Route path="/creator/:id" element={<CreatorProfile />} />
              <Route path="/creators" element={<Creators />} />
              <Route path="/login" element={<Auth mode="login" />} />
              <Route path="/signup" element={<Auth mode="signup" />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/auth/callback/:provider" element={<OAuthCallback />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/pro/thanks" element={<PaymentResult kind="success" />} />
              <Route path="/pro/cancelled" element={<PaymentResult kind="cancel" />} />
              <Route path="/docs" element={<Docs />} />
              <Route path="/docs/:slug" element={<Docs />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/contact" element={<Contact />} />

              <Route path="/publish" element={<RequireAuth><Publish /></RequireAuth>} />
              <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
              <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
              <Route path="/workflows" element={<RequireAuth><Workflows /></RequireAuth>} />
              <Route path="/workflows/:wfId" element={<RequireAuth><WorkflowEditor /></RequireAuth>} />
              <Route path="/moderation" element={<RequireAuth><Moderation /></RequireAuth>} />

              <Route path="*" element={<NotFound />} />
            </Routes>
              </React.Suspense>
            </ErrorBoundary>
          </main>
          <Footer />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}
